import moment from 'moment';

import {TooltipItemType, TooltipRow} from 'common/components/chart-tooltip/types';
import {getDataGroupName, isSegmented} from 'common/report/retention/util/format';
import {FormattedRow, LiftCell, LiftRow, RawCell, RetentionBookmarkParams} from 'common/report/retention/util/types';
import {getFrequencyTooltipSubtitle, getRetentionBucketName, isAddiction} from 'common/report/retention/util';
import {TimeUnit} from 'common/widgets/date-range-picker/models/types';
import {SegmentKeyToPath} from 'common/report/retention/models/types';
import {TEXT_HIGHLIGHT_THEME} from 'common/components/text-highlight';
import {getLiftBaselineSegmentRawHead} from 'common/report/retention/util/parsing';
import {formatLiftPercent} from 'common/report/util/lift';

export function getToolTipSections(
  row: FormattedRow,
  cellIndex: number,
  bookmarkParams: RetentionBookmarkParams,
  comparisonRow?: FormattedRow,
  liftRow?: LiftRow,
  liftBaselineRow?: FormattedRow,
  segmentKeyToPath?: SegmentKeyToPath,
) {
  const cell = row.data[cellIndex];

  const comparisonRowCell = comparisonRow?.data[cellIndex];
  const liftRowCell = liftRow?.data[cellIndex];
  const liftBaselineRowCell = liftBaselineRow?.data[cellIndex];

  const dataGroupName = getDataGroupName(bookmarkParams.data_group_id);

  const comparisonFormatType = TooltipItemType.Text;
  return [
    {
      rows: [
        ...getHeaderRows(bookmarkParams, row),

        {
          items:
            comparisonRow && !liftRow
              ? []
              : [
                  {text: `Users entering ${bookmarkParams.date_unit} of`, type: comparisonFormatType},
                  {text: moment(cell.date.valueOf()).format(`MMM D YYYY`), type: comparisonFormatType},
                ],
        },
      ],
    },
    ...(liftRow
      ? getLiftChartRowSections(
          cell,
          row,
          comparisonRowCell,
          liftRowCell,
          liftBaselineRowCell,
          bookmarkParams,
          segmentKeyToPath,
        )
      : [...getChartRowSections(cell, row), ...getChartRowSections(comparisonRowCell, comparisonRow, true)]),
  ];

  function getChartRowSections(cell: RawCell, row: FormattedRow<RawCell>, isComparisonRow = false) {
    const hasComparison = !!comparisonRow;
    if (!cell || !row) {
      return [];
    }
    return [
      {
        ...(hasComparison
          ? {
              bulletColor: row.selection_color,
              bulletStyle: !isComparisonRow ? `solid` : `dash`,
            }
          : {}),
        rows: [
          {
            items: hasComparison
              ? [
                  {text: `Users entering ${bookmarkParams.date_unit} of`, type: comparisonFormatType},
                  {text: moment(cell.date.valueOf()).format(`MMM D YYYY`), type: comparisonFormatType},
                ]
              : [],
          },
          {
            items: [
              {text: cell.formatted_percent, type: TooltipItemType.Data},
              {
                text: bookmarkParams.retention_type === `addiction` ? `engaged` : `retention`,
                type: TooltipItemType.Text,
              },
            ],
          },
          {
            items: [
              {text: cell.formatted_value, type: TooltipItemType.Data},
              {
                text: `${dataGroupName}s`,
                type: TooltipItemType.Text,
              },
            ],
          },
        ],
      },
    ];
  }
}

function getLiftChartRowSections(
  cell: RawCell,
  row: FormattedRow,
  comparisonRowCell: RawCell,
  liftRowCell: LiftCell,
  liftBaselineRowCell: RawCell,
  bookmarkParams: RetentionBookmarkParams,
  segmentKeyToPath: SegmentKeyToPath,
) {
  if (!cell || !row) {
    return [];
  }

  const hasComparison = !!comparisonRowCell;

  const baselineSegmentName = hasComparison
    ? moment(comparisonRowCell.date.valueOf()).format(`MMM D YYYY`)
    : getLiftBaselineSegmentRawHead(row, segmentKeyToPath, bookmarkParams.lift_comparison);

  const currentSegmentName = hasComparison ? moment(cell.date.valueOf()).format(`MMM D YYYY`) : (row.head as string);
  const baselineRowCell = hasComparison ? comparisonRowCell : liftBaselineRowCell;

  let liftDisplayValue, currentSegmentReferenceValue, baselineSegmentAbsValue;
  let retentionAddictionLabel;
  let isNegative = false;

  if (bookmarkParams.percentage_view) {
    liftDisplayValue = liftRowCell.formatted_percent;
    isNegative = liftRowCell.percent < 0;
    currentSegmentReferenceValue = cell.formatted_percent;
    baselineSegmentAbsValue = baselineRowCell?.formatted_percent;
    retentionAddictionLabel = [
      {
        text: isAddiction(bookmarkParams) ? `engaged` : `retention`,
        type: TooltipItemType.Text,
      },
    ];
  } else {
    liftDisplayValue = formatLiftPercent(liftRowCell.value);
    isNegative = liftRowCell.value < 0;
    currentSegmentReferenceValue = cell.formatted_value;
    baselineSegmentAbsValue = baselineRowCell?.formatted_value;
    retentionAddictionLabel = [];
  }

  return [
    {
      rows: [
        {
          items: [
            {
              text: liftDisplayValue,
              type: TooltipItemType.Data,
              theme: isNegative ? TEXT_HIGHLIGHT_THEME.NEGATIVE : TEXT_HIGHLIGHT_THEME.POSITIVE,
            },
          ],
        },
        {
          items: [
            {text: currentSegmentReferenceValue, type: TooltipItemType.Data},
            ...retentionAddictionLabel,
            {
              text: currentSegmentName,
              type: TooltipItemType.Text,
            },
          ],
        },
        ...(baselineSegmentName !== currentSegmentName
          ? [
              {
                items: [
                  {text: baselineSegmentAbsValue, type: TooltipItemType.Data},
                  ...retentionAddictionLabel,
                  {
                    text: baselineSegmentName,
                    type: TooltipItemType.Text,
                  },
                ],
              },
            ]
          : []),
      ],
    },
  ];
}

function getHeaderRows(bookmarkParams: RetentionBookmarkParams, row: FormattedRow): TooltipRow[] {
  const subtitle = isAddiction(bookmarkParams)
    ? getFrequencyTooltipSubtitle({
        cumulative: bookmarkParams.cumulative,
        dateUnit: bookmarkParams.date_unit as TimeUnit,
        bucketIndex: bookmarkParams.retention_bucket,
      })
    : getRetentionBucketName({
        bucketIndex: bookmarkParams.retention_bucket,
        retentionType: bookmarkParams.retention_type,
        retentionUnit: bookmarkParams.retention_unit,
        dateUnit: bookmarkParams.date_unit,
        bucketSizes: bookmarkParams.bucket_sizes,
      });

  let result;

  if (isSegmented(bookmarkParams.group_by)) {
    result = [
      {
        items: [
          {
            text: row.head as string,
            type: TooltipItemType.Strong,
          },
        ],
      },
      {
        items: [{text: subtitle, type: TooltipItemType.Text}],
      },
    ];
  } else {
    result = [
      {
        items: [{text: subtitle, type: TooltipItemType.Strong}],
      },
    ];
  }

  return result;
}
